/**
 * A porta entre a tela de ordens de serviço e a API do módulo 06.
 *
 * Só caminho de rota e formato de JSON; o `fetch` e a queda de erro moram em
 * `@/lib/pedido`.
 */
import { comCorpo, pedir } from '@/lib/pedido';

export type TipoDeOrdem = 'preventiva' | 'corretiva' | 'inspecao' | 'campanha' | 'melhoria' | 'terceiro';

export type Etapa =
  | 'solicitada'
  | 'triagem'
  | 'diagnostico'
  | 'planejada'
  | 'aguardando_peca'
  | 'em_execucao'
  | 'teste'
  | 'liberada'
  | 'encerrada'
  | 'cancelada';

export interface OrdemDaApi {
  readonly id: string;
  readonly numero: number;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly equipamentoDescricao: string;
  readonly tipo: TipoDeOrdem;
  readonly prioridade: number;
  readonly etapa: Etapa;
  readonly sintoma: string;
  readonly hipoteseIa: string | null;
  readonly diagnostico: string | null;
  readonly causaRaiz: string | null;
  readonly solucao: string | null;
  readonly maquinaParada: boolean;
  readonly paradaDesde: string | null;
  readonly paradaAte: string | null;
  readonly responsavelId: string | null;
  readonly responsavel: string | null;
  readonly solicitadaPor: string | null;
  readonly abertaEm: string;
  readonly liberadaPor: string | null;
  readonly liberadaEm: string | null;
  readonly leituraNaLiberacao: number | null;
  readonly evidenciaLiberacao: string | null;
  readonly encerradaEm: string | null;
  readonly motivoCancelamento: string | null;
  readonly etapaDesde: string | null;
  readonly horasDeMaoDeObra: number;
  /**
   * 0101: o tipo só libera e encerra com diagnóstico e causa raiz (hoje, a
   * corretiva). Ausente em servidor anterior a esta mudança.
   */
  readonly exigeCausa?: boolean;
  /**
   * 0101: liberada ou encerrada sem diagnóstico e causa raiz — a que fechou
   * antes da regra. A liberada se completa no encerramento; a encerrada não muda.
   */
  readonly semCausa?: boolean;
  /**
   * A peca que a O.S. espera: a primeira prevista sem reserva no estoque, com
   * o pedido de compra dela (M17). `null` quando nao espera peca; ausente em
   * servidor anterior a esta mudanca.
   */
  readonly pecaEsperada?: PecaEsperada | null;
  /**
   * A parada combinada com a operação (0089). `null` sem programação; ausente
   * em servidor anterior a esta mudança.
   */
  readonly paradaProgramada?: ParadaProgramada | null;
}

export interface ParadaProgramada {
  /** Instantes ISO. */
  readonly de: string;
  readonly ate: string;
  readonly combinadaComId: string;
  readonly combinadaCom: string | null;
  readonly combinadaPor: string | null;
  readonly combinadaEm: string;
}

/** Uma linha da agenda de paradas (GET /oficina/paradas-programadas). */
export interface ParadaDaAgenda {
  readonly ordemId: string;
  readonly numero: number;
  readonly tipo: TipoDeOrdem;
  readonly etapa: Etapa;
  readonly equipamentoId: string;
  readonly equipamento: string;
  readonly equipamentoDescricao: string;
  readonly de: string;
  readonly ate: string;
  readonly combinadaCom: string | null;
  readonly combinadaPor: string | null;
  /** R06 (p.70): o serviço da O.S. (o sintoma). Ausente em servidor anterior. */
  readonly servico?: string;
}

export interface EnvioDaParada {
  /** Instantes ISO com fuso. */
  readonly de: string;
  readonly ate: string;
  readonly combinadaComId: string;
}

export interface PecaEsperada {
  readonly descricao: string;
  /** O numero do pedido de compra, quando a requisicao da O.S. ja virou pedido. */
  readonly pedido: string | null;
  /** `aaaa-mm-dd`: a emissao mais o prazo do fornecedor. */
  readonly chegaEm: string | null;
}

export interface PassagemDeEtapa {
  readonly etapa: Etapa;
  readonly desde: string;
  readonly ate: string | null;
  readonly horas: number;
  readonly natureza: 'espera' | 'reparo' | 'fechamento';
  readonly registradoPor: string | null;
  readonly motivo: string | null;
}

export interface DetalheDaOrdem {
  readonly ordem: OrdemDaApi;
  /**
   * A última leitura do medidor da máquina (31/10/2026), para a etiqueta ao
   * lado do campo da liberação. Ausente em servidor anterior a esta mudança.
   */
  readonly leituraDaMaquina?: { readonly valor: number; readonly lidaEm: string; readonly tipoMedidor: string } | null;
  readonly etapas: readonly PassagemDeEtapa[];
  readonly maoDeObra: readonly {
    id: string;
    mecanico: string;
    inicio: string;
    fim: string;
    horas: number;
    descricao: string | null;
  }[];
  readonly pecas: readonly {
    id: string;
    produto: string | null;
    descricao: string;
    quantidade: number;
    unidade: string;
    situacao: 'prevista' | 'aplicada';
    deposito: string | null;
    reservada: boolean;
    baixouEstoque: boolean;
    pedido?: string | null;
    chegaEm?: string | null;
    /** 0088 · aviso, nunca bloqueio: a peça não tem aplicação validada para a máquina da O.S. */
    semAplicacaoValidada?: boolean;
  }[];
  readonly servicos: readonly { id: string; fornecedor: string; descricao: string; valor: number | null; documento: string | null }[];
  readonly tarefas: readonly { tarefaId: string; descricao: string; gatilho: string; intervalo: number }[];
  /** Foto e anexo (migração 0026): evidência, só de inserção. */
  readonly anexos: readonly AnexoDaOrdem[];
}

export interface AnexoDaOrdem {
  readonly id: string;
  readonly descricao: string;
  /** A etapa em que a O.S. estava quando o anexo entrou: é o "antes" e o "depois". */
  readonly etapa: Etapa;
  readonly mime: string;
  readonly tamanhoBytes: number;
  readonly enviadoEm: string;
  readonly enviadoPor: string | null;
}

export interface CadastrosDaOficina {
  /** O código interno e a placa vão nas etiquetas do seletor da máquina (31/10/2026). */
  readonly equipamentos: readonly { id: string; codigo: string; descricao: string; codigoInterno?: string | null; placa?: string | null }[];
  readonly pessoas: readonly { id: string; nome: string }[];
  readonly fornecedores: readonly { id: string; codigo: string; nome: string }[];
  readonly produtos: readonly { id: string; codigo: string; nome: string; unidade: string }[];
  readonly depositos: readonly { id: string; codigo: string; nome: string }[];
}

export interface EnvioDeOrdem {
  readonly equipamentoId: string;
  readonly tipo: TipoDeOrdem;
  readonly prioridade: number;
  readonly sintoma: string;
  readonly maquinaParada: boolean;
  readonly tarefaIds?: readonly string[];
}

export interface EnvioDeLiberacao {
  readonly solucao: string;
  readonly evidencia: string;
  readonly leitura?: number | null;
  /** 0101: a corretiva só libera com os dois; ausentes, valem os que a O.S. já tem. */
  readonly diagnostico?: string;
  readonly causaRaiz?: string;
}

/** 0101: a liberada sem causa (de antes da regra) se completa no encerramento. */
export interface EnvioDeEncerramento {
  readonly diagnostico?: string;
  readonly causaRaiz?: string;
}

const da = (id: string) => `/oficina/ordens/${encodeURIComponent(id)}`;

export const api = {
  cadastros: () => pedir<CadastrosDaOficina>('/oficina/cadastros'),
  ordens: () => pedir<{ ordens: readonly OrdemDaApi[] }>('/oficina/ordens'),
  ordem: (id: string) => pedir<DetalheDaOrdem>(da(id)),
  abrir: (envio: EnvioDeOrdem) => comCorpo<{ id: string; numero: number }>('POST', '/oficina/ordens', envio),
  ajustar: (id: string, envio: { diagnostico?: string | null; causaRaiz?: string | null; responsavelId?: string | null }) =>
    comCorpo<{ id: string }>('PATCH', da(id), envio),
  mudarEtapa: (id: string, etapa: Etapa, motivo?: string) =>
    comCorpo<{ etapa: Etapa }>('POST', `${da(id)}/etapa`, motivo ? { etapa, motivo } : { etapa }),
  maoDeObra: (id: string, envio: { mecanicoId: string; inicio: string; fim: string; descricao?: string | null }) =>
    comCorpo<{ id: string }>('POST', `${da(id)}/mao-de-obra`, envio),
  peca: (
    id: string,
    envio: {
      produtoId?: string | null;
      depositoId?: string | null;
      descricao: string;
      quantidade: number;
      unidade: string;
      situacao: 'prevista' | 'aplicada';
    },
  ) => comCorpo<{ id: string }>('POST', `${da(id)}/pecas`, envio),
  aplicarPeca: (pecaId: string) =>
    comCorpo<{ situacao: string }>('POST', `/oficina/pecas/${encodeURIComponent(pecaId)}/aplicacao`, {}),
  servico: (id: string, envio: { fornecedorId: string; descricao: string; valor?: number | null; documento?: string | null }) =>
    comCorpo<{ id: string }>('POST', `${da(id)}/servicos`, envio),
  liberar: (id: string, envio: EnvioDeLiberacao) => comCorpo<{ etapa: Etapa }>('POST', `${da(id)}/liberacao`, envio),
  /** A parada combinada com a operação (0089). Gravar de novo reprograma. */
  programarParada: (id: string, envio: EnvioDaParada) =>
    comCorpo<{ id: string; de: string; ate: string }>('POST', `${da(id)}/parada-programada`, envio),
  /** A agenda: as paradas que cruzam o dia (hoje, sem data). */
  paradasProgramadas: (dia?: string) =>
    pedir<{ paradas: readonly ParadaDaAgenda[] }>(
      `/oficina/paradas-programadas${dia ? `?de=${encodeURIComponent(dia)}&ate=${encodeURIComponent(dia)}` : ''}`,
    ),
  encerrar: (id: string, envio: EnvioDeEncerramento = {}) => comCorpo<{ etapa: Etapa }>('POST', `${da(id)}/encerramento`, envio),
  cancelar: (id: string, motivo: string) => comCorpo<{ etapa: Etapa }>('POST', `${da(id)}/cancelamento`, { motivo }),
  /** O arquivo vai cru no corpo; a descrição, no endereço. O servidor lê o tipo do conteúdo. */
  anexar: (id: string, arquivo: File, descricao: string) =>
    pedir<{ id: string }>(`${da(id)}/anexos?descricao=${encodeURIComponent(descricao)}`, {
      method: 'POST',
      body: arquivo,
      headers: { 'content-type': arquivo.type || 'application/octet-stream' },
    }),
  /** IA 03: a hipótese a partir do sintoma, citando os documentos da máquina. */
  pedirHipotese: (id: string) =>
    comCorpo<{ id?: string; sabe: boolean; resposta: string; falta: string | null; gravadaNaOrdem: boolean }>('POST', `${da(id)}/hipotese`, {}),
  /** Uma URL que vale alguns minutos, e não o arquivo. */
  urlDoAnexo: (anexoId: string) =>
    pedir<{ url: string; expiraEmSegundos: number; mime: string }>(`/oficina/anexos/${encodeURIComponent(anexoId)}/arquivo`),
};
